import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import { toast } from "sonner";
import { useCallback } from "react";
import { useCreateFolderActions } from "~/store/createFolderStore";
import { useDialogActions } from "~/store/dialogStore";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { base64ToUint8Array, useEncryption } from "~/hooks/useEncryption";
import { EncryptedPayload, FolderPayload, MoveFilesPayload, MergeFoldersPayload } from "~/types/global";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import {
  deleteFolder,
  renameFolder,
  createFolder,
  moveFilesBetweenFolders,
  moveFolderToFolder,
  mergeFolders,
  getHierarchicalFolders,
} from "~/services/gateways/folder.gateway";
import { REPORT_CONSTANTS } from "~/helpers/constants";
import { useIsPasswordExpired } from "~/store/credentials";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { usePassword } from "~/store/credentials";
import { useOrgPublicKey } from "~/hooks/useOrgEncryption";
import { generatePerReportDek, wrapDekForUser, bufferToBase64 } from "~/helpers/envelopeCrypto";
import { importRsaPublicKey, rsaOaepWrapDek } from "~/helpers/orgCrypto";
import { useEnvelopeEncryption } from "~/hooks/useEnvelopeEncryption";

export interface CreateFolderRequest {
  folderName: string;
  selectedReports?: string[];
  parentFolderId?: string | null;
  onSuccess?: (data: CreateFolderResponse) => void;
}

export type CreateFolderMutationPayload = CreateFolderRequest;

export interface CreateFolderResponse {
  folder_id: string;
  folder_name: string;
  created_at: string;
  reports_count: number;
}

export interface FolderListQueryParams {
  folder_id?: string;
  hmac_folder_name?: string;
  excludeFolderId?: string;
}

interface MutationSuccessHandlers {
  onFolderCreate: (data: CreateFolderResponse) => void;
  onFolderDelete: () => void;
  onFolderRename: () => void;
  onFilesMove: () => void;
  onReportMove: () => void;
  onFolderMove: () => void;
  onFolderMerge: () => void;
}

export const useFolderCRUD = () => {
  const { folderId } = useParams<{ folderId?: string }>();
  const { invalidateCurrentFolderNoFilters } = useReportCRUD(folderId || null);
  const { closeDialog } = useDialogActions();
  const { /* encryptData, */ _encryptData, encryptNgrams } = useEncryption();
  const { data: orgPublicKey } = useOrgPublicKey();
  const { createItemDecryptor } = useEnvelopeEncryption();
  const { clearFolderData } = useCreateFolderActions();
  const queryClient = useQueryClient();
  // SECRET KEY
  const isPasswordExpired = useIsPasswordExpired();
  const { withPasswordCheck } = usePasswordCheck();
  const secretKey = usePassword();

  const invalidateHierarchicalFoldersQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["hierarchical-folders"],
      exact: false,
    });
  }, [queryClient]);

  const handleFolderCreatedSuccess = useCallback((data: CreateFolderResponse, options?: { skipDialogClose?: boolean }) => {
    clearFolderData();

    if (!options?.skipDialogClose) {
      closeDialog();
    }

    toast.success(`Pasta criada com sucesso!`);

    withPasswordCheck(() => {
      invalidateCurrentFolderNoFilters(folderId || null);
      invalidateHierarchicalFoldersQuery();
    });
  }, [folderId, withPasswordCheck, invalidateCurrentFolderNoFilters, clearFolderData, closeDialog, invalidateHierarchicalFoldersQuery]);

  const successHandlers: MutationSuccessHandlers = {
    onFolderCreate: useCallback((data: CreateFolderResponse) => {
      handleFolderCreatedSuccess(data);
    }, [handleFolderCreatedSuccess]),

    onFolderDelete: useCallback(() => {
      closeDialog();
      toast.success(`Pasta deletada com sucesso!`);
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    }, [folderId, withPasswordCheck, invalidateCurrentFolderNoFilters, closeDialog]),

    onFolderRename: useCallback(() => {
      closeDialog();
      toast.success(`Pasta renomeada com sucesso!`);
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    }, [folderId, withPasswordCheck, invalidateCurrentFolderNoFilters, closeDialog]),

    onFilesMove: useCallback(() => {
      closeDialog();
      toast.success(`Arquivos movidos com sucesso!`);
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    }, [folderId, withPasswordCheck, invalidateCurrentFolderNoFilters, closeDialog]),

    onReportMove: useCallback(() => {
      closeDialog();
      toast.success(`Relatório movido com sucesso!`);
    }, [closeDialog]),

    onFolderMove: useCallback(() => {
      closeDialog();
      toast.success(`Pasta movida com sucesso!`);
    }, [closeDialog]),

    onFolderMerge: useCallback(() => {
      closeDialog();
      toast.success(`Pastas combinadas com sucesso!`);
    }, [closeDialog]),
  };

  const createFolderMutation = useMutation<CreateFolderResponse, Error, CreateFolderMutationPayload>({
    mutationFn: async (payload: CreateFolderMutationPayload): Promise<CreateFolderResponse> => {
      const folderName = payload.folderName;

      let encryptedFolderName;
      let envelopeFields: { user_wrapped_dek?: string; user_dek_iv?: string; org_wrapped_dek?: string; key_version?: number } = {};

      if (orgPublicKey) {
        const perFolderDek = generatePerReportDek();
        encryptedFolderName = await _encryptData(folderName, perFolderDek.buffer as ArrayBuffer);

        const userDek = base64ToUint8Array(secretKey || "");
        const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
        const wrapped = await wrapDekForUser(rawUserKey, perFolderDek);
        const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
        const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, perFolderDek);

        envelopeFields = {
          user_wrapped_dek: wrapped.user_wrapped_dek,
          user_dek_iv: wrapped.user_dek_iv,
          org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
          key_version: orgPublicKey.key_version,
        };
      } else {
        const derivedKey = base64ToUint8Array(secretKey || "");
        encryptedFolderName = await _encryptData(folderName, derivedKey.buffer);
      }

      if (!encryptedFolderName?.success || !encryptedFolderName.data) {
        throw new Error("Erro ao criptografar nome da pasta");
      }

      const hmacResult = await encryptNgrams({ folder_name: folderName });
      const apiPayload: FolderPayload = {
        [REPORT_CONSTANTS.new_folder.folder_name]: encryptedFolderName.data as EncryptedPayload,
        [REPORT_CONSTANTS.new_folder.parent_folder_id]: payload.parentFolderId !== undefined ? payload.parentFolderId : (folderId ?? null),
        [REPORT_CONSTANTS.new_folder.user_reports_id_list]: payload.selectedReports || [],
        [REPORT_CONSTANTS.new_folder.hmac_folder_name]: hmacResult.folder_name || [],
        ...envelopeFields,
      };

      return createFolder(apiPayload) as Promise<CreateFolderResponse>;
    },
    onSuccess: (data, variables) => {
      if (!variables.onSuccess) {
        successHandlers.onFolderCreate(data);
      } else {
        handleFolderCreatedSuccess(data, { skipDialogClose: true }); // sem fechar o dialog
        variables.onSuccess(data);
      }
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar criar a pasta",
      "Erro ao criar pasta"
    ),
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => deleteFolder(folderId),
    onSuccess: successHandlers.onFolderDelete,
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar deletar a pasta",
      "Erro ao deletar pasta"
    ),
  });

  const renameFolderMutation = useMutation({
    mutationFn: async (payload: FolderPayload) => {
      const hmacResult = await encryptNgrams({ folder_name: payload.folder_name });

      let encryptedFolderName;
      let envelopeFields: { user_wrapped_dek?: string; user_dek_iv?: string; org_wrapped_dek?: string; key_version?: number } = {};

      if (orgPublicKey) {
        const perFolderDek = generatePerReportDek();
        encryptedFolderName = await _encryptData(payload.folder_name, perFolderDek.buffer as ArrayBuffer);

        const userDek = base64ToUint8Array(secretKey || "");
        const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
        const wrapped = await wrapDekForUser(rawUserKey, perFolderDek);
        const rsaPubKey = await importRsaPublicKey(orgPublicKey.public_key);
        const orgWrappedBuf = await rsaOaepWrapDek(rsaPubKey, perFolderDek);

        envelopeFields = {
          user_wrapped_dek: wrapped.user_wrapped_dek,
          user_dek_iv: wrapped.user_dek_iv,
          org_wrapped_dek: bufferToBase64(new Uint8Array(orgWrappedBuf)),
          key_version: orgPublicKey.key_version,
        };
      } else {
        const derivedKey = base64ToUint8Array(secretKey || "");
        encryptedFolderName = await _encryptData(payload.folder_name, derivedKey.buffer);
      }

      if (!encryptedFolderName?.success || !encryptedFolderName.data) {
        throw new Error("Erro ao criptografar nome da pasta");
      }

      return renameFolder({
        [REPORT_CONSTANTS.new_folder.folder_id]: payload.folder_id,
        [REPORT_CONSTANTS.new_folder.folder_name]: encryptedFolderName.data as EncryptedPayload,
        [REPORT_CONSTANTS.new_folder.hmac_folder_name]: hmacResult.folder_name || [],
        ...envelopeFields,
      });
    },
    onSuccess: successHandlers.onFolderRename,
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar renomear a pasta",
      "Erro ao renomear pasta"
    ),
  });

  const moveReportToFolderMutation = useMutation({
    mutationFn: async (payload: {
      reportId: string;
      sourceFolderId?: string | null;
      destinationFolderId?: string | null;
    }) => {
      const movePayload: MoveFilesPayload = {
        [REPORT_CONSTANTS.move_files.src_folder_id]: payload.sourceFolderId || null,
        [REPORT_CONSTANTS.move_files.dest_folder_id]: payload.destinationFolderId || null,
        [REPORT_CONSTANTS.move_files.user_reports_id_list]: [payload.reportId],
        [REPORT_CONSTANTS.move_files.folder_id_list]: null,
      };

      console.log("[useFolderCRUD] moveReportToFolderMutation payload", movePayload);
      return moveFilesBetweenFolders(movePayload);
    },
    onSuccess: (data, variables) => {
      successHandlers.onReportMove();
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(variables.sourceFolderId || null);
        if (variables.destinationFolderId !== variables.sourceFolderId) {
          invalidateCurrentFolderNoFilters(variables.destinationFolderId || null);
        }
      });
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar mover o relatório",
      "Erro ao mover relatório"
    ),
  });

  const moveFolderToFolderMutation = useMutation({
    mutationFn: async (payload: {
      folderId: string;
      sourceFolderId?: string | null;
      destinationFolderId?: string | null;
    }) => {
      const movePayload: MoveFilesPayload = {
        [REPORT_CONSTANTS.move_files.src_folder_id]: payload.sourceFolderId || null,
        [REPORT_CONSTANTS.move_files.dest_folder_id]: payload.destinationFolderId || null,
        [REPORT_CONSTANTS.move_files.user_reports_id_list]: null,
        [REPORT_CONSTANTS.move_files.folder_id_list]: [payload.folderId],
      };

      console.log("[useFolderCRUD] moveFolderToFolderMutation payload", movePayload);
      return moveFolderToFolder(movePayload);
    },
    onSuccess: (data, variables) => {
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(variables.sourceFolderId || null);
        if (variables.destinationFolderId !== variables.sourceFolderId) {
          invalidateCurrentFolderNoFilters(variables.destinationFolderId || null);
        }
        successHandlers.onFolderMove();
      });
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar mover a pasta",
      "Erro ao mover pasta"
    ),
  });

  const mergeFoldersMutation = useMutation({
    mutationFn: async (payload: {
      folderId: string | null;
      folderIdToMerge: string;
    }) => {
      const mergePayload: MergeFoldersPayload = {
        [REPORT_CONSTANTS.merge_folders.folder_id]: payload.folderId,
        [REPORT_CONSTANTS.merge_folders.folder_id_to_merge]: payload.folderIdToMerge,
      };

      console.log("[useFolderCRUD] mergeFoldersMutation payload", mergePayload);
      return mergeFolders(mergePayload);
    },
    onSuccess: () => {
      successHandlers.onFolderMerge();
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar combinar as pastas",
      "Erro ao combinar pastas"
    ),
  });

  function useHierarchicalFoldersQuery(excludeFolderId?: string, options?: { enabled?: boolean; refetchOnWindowFocus?: boolean; staleTime?: number }) {
    return useQuery({
      queryKey: ["hierarchical-folders", excludeFolderId],
      queryFn: async () => {
        const folders = await getHierarchicalFolders({
          column_order: "created_at",
          order: "desc",
        });

        // Decrypt folder names recursively
        const decryptFolder = async (folder: any): Promise<any> => {
          let folderName = folder.folder_name;
          if (folderName && typeof folderName === "object" && "encrypted" in folderName) {
            const decryptor = createItemDecryptor(folder);
            const result = await decryptor(folderName);
            folderName = result.success ? result.data : folder.folder_id.slice(-8);
          }
          const children = folder.data && Array.isArray(folder.data)
            ? await Promise.all(folder.data.map(decryptFolder))
            : folder.data;
          return { ...folder, folder_name: folderName, data: children };
        };

        return Array.isArray(folders) ? Promise.all(folders.map(decryptFolder)) : folders;
      },
      retry: false,
      enabled: options?.enabled ?? !isPasswordExpired,
      refetchOnWindowFocus: options?.refetchOnWindowFocus,
      staleTime: options?.staleTime,
    });
  }

  return {
    createFolderMutation,
    deleteFolderMutation,
    renameFolderMutation,
    moveReportToFolderMutation,
    moveFolderToFolderMutation,
    mergeFoldersMutation,
    useHierarchicalFoldersQuery,
    invalidateHierarchicalFoldersQuery,
    handleFolderCreatedSuccess,
  };
};
